/**
 * /knowledge — pastas da base de conhecimento (réplica da tela "Conhecimentos"
 * do Tiflux): header com ações, busca central e lista vertical de pastas;
 * com busca preenchida, vira lista de artigos ("Resultados").
 */
import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, Copy, ExternalLink, Globe, Pencil, Plus, Search, Settings, Trash2 } from 'lucide-react';
import type { KnowledgeFolder } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDebouncedValue } from '@/features/tickets/use-debounced-value';
import {
  errorMessage,
  knowledgeFoldersOptions,
  knowledgeListOptions,
} from '@/features/knowledge/knowledge-queries';
import { knowledgeCountLabel } from '@/features/knowledge/knowledge-format';
import { KnowledgeArticleList } from '@/features/knowledge/knowledge-article-list';
import {
  DeleteFolderDialog,
  EditFolderDialog,
  NewFolderDialog,
} from '@/features/knowledge/folder-dialogs';

function FolderCard({
  folder,
  onEdit,
  onDelete,
}: {
  folder: KnowledgeFolder;
  onEdit: (folder: KnowledgeFolder) => void;
  onDelete: (folder: KnowledgeFolder) => void;
}) {
  return (
    <div className="group flex items-start gap-3 rounded-lg border bg-card p-4 shadow-xs">
      <span className="text-[28px] leading-none" aria-hidden>
        {folder.icon || '📁'}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[15px] font-semibold">{folder.name}</div>
        {folder.description ? (
          <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{folder.description}</p>
        ) : null}
        <Link
          to="/knowledge/folder/$folderId"
          params={{ folderId: String(folder.id) }}
          className="mt-1 inline-block text-sm font-medium text-primary transition-colors hover:text-primary-pressed"
        >
          {knowledgeCountLabel(folder.knowledgeCount)}
        </Link>
      </div>
      <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <button
          type="button"
          onClick={() => onEdit(folder)}
          aria-label={`Editar pasta ${folder.name}`}
          className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-primary"
        >
          <Pencil className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(folder)}
          aria-label={`Excluir pasta ${folder.name}`}
          className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-destructive"
        >
          <Trash2 className="size-4" />
        </button>
      </div>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div aria-busy className="flex flex-col gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-start gap-3 rounded-lg border bg-card p-4 shadow-xs">
          <Skeleton className="size-8 rounded-md" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-3.5 w-3/4" />
            <Skeleton className="h-3.5 w-40" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function KnowledgeFoldersPage() {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const search = useDebouncedValue(term.trim(), 300);
  const searching = search.length > 0;

  const folders = useQuery(knowledgeFoldersOptions());
  const results = useQuery({ ...knowledgeListOptions({ search }), enabled: searching });

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [editFolder, setEditFolder] = useState<KnowledgeFolder | null>(null);
  const [deleteFolder, setDeleteFolder] = useState<KnowledgeFolder | null>(null);
  const [copied, setCopied] = useState(false);

  const copyPublicLink = () => {
    void navigator.clipboard.writeText(`${window.location.origin}/kb`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="space-y-6">
      {/* Header: título + ações à direita */}
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-xl leading-7 font-semibold">Conhecimentos</h1>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="icon-sm"
            aria-label="Nova pasta"
            title="Nova pasta"
            onClick={() => setNewFolderOpen(true)}
          >
            <Settings className="size-4" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="text-primary hover:text-primary-pressed">
                {copied ? <Check className="size-4" /> : <Globe className="size-4" />}
                {copied ? 'Link copiado' : 'Base pública'}
                <ChevronDown className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => window.open('/kb', '_blank')}>
                <ExternalLink />
                Acessar a base
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={copyPublicLink}>
                <Copy />
                Copiar link
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button size="sm" onClick={() => void navigate({ to: '/knowledge/new', search: {} })}>
            <Plus className="size-4" strokeWidth={2.5} />
            Conhecimento
          </Button>
        </div>
      </div>

      {/* Busca central */}
      <div className="relative mx-auto w-full max-w-2xl">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Busque por uma pasta ou conhecimento"
          aria-label="Buscar na base de conhecimento"
          className="bg-card pl-9"
        />
      </div>

      {/* Corpo: pastas (busca vazia) ou resultados de artigos */}
      <div className="mx-auto w-full max-w-2xl">
        {searching ? (
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-muted-foreground">Resultados</h2>
            {results.isPending ? (
              <ListSkeleton />
            ) : results.isError ? (
              <EmptyState message={`Erro na busca: ${errorMessage(results.error)}`} />
            ) : results.data.length === 0 ? (
              <EmptyState message="Nenhum conhecimento encontrado para a busca" />
            ) : (
              <KnowledgeArticleList items={results.data} />
            )}
          </div>
        ) : folders.isPending ? (
          <ListSkeleton />
        ) : folders.isError ? (
          <EmptyState message={`Erro ao carregar pastas: ${errorMessage(folders.error)}`} />
        ) : folders.data.length === 0 ? (
          <EmptyState message="Nenhuma pasta criada — use a engrenagem para criar a primeira" />
        ) : (
          <div className="flex flex-col gap-3">
            {folders.data.map((folder) => (
              <FolderCard
                key={folder.id}
                folder={folder}
                onEdit={setEditFolder}
                onDelete={setDeleteFolder}
              />
            ))}
          </div>
        )}
      </div>

      {newFolderOpen ? <NewFolderDialog onClose={() => setNewFolderOpen(false)} /> : null}
      {editFolder ? <EditFolderDialog folder={editFolder} onClose={() => setEditFolder(null)} /> : null}
      {deleteFolder ? (
        <DeleteFolderDialog folder={deleteFolder} onClose={() => setDeleteFolder(null)} />
      ) : null}
    </div>
  );
}
